import React, { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import Table from "../shared/table/Table";
import { FiEye, FiEdit3, FiTrash2, FiPlus, FiSave } from "react-icons/fi";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import useUserLanguage from "../../hooks/useUserLanguage";
import useTranslations from "../../hooks/useTranslations";
import { confirmAlert } from "react-confirm-alert";
import "react-confirm-alert/src/react-confirm-alert.css";
import usePermission from "../../hooks/usePermission";

const BASE_URL = `${process.env.REACT_APP_API_BASE_URL}/api/definitions`;


// Durum Switch Bileşeni
const StatusSwitch = React.memo(({ redirectId, currentStatus, fetchRedirects }) => {
  const [isChecked, setIsChecked] = useState(currentStatus === "active");

  const toggleStatus = async () => {
    const newStatus = isChecked ? "inactive" : "active";
    setIsChecked(!isChecked);
    try {
      await axios.put(`${BASE_URL}/update-status/${redirectId}`, { status: newStatus });
      fetchRedirects();
    } catch (error) {
      console.error("❌ Durum güncellenemedi:", error);
      setIsChecked(isChecked);
      toast.error("Durum güncellenemedi!");
    }
  };

  return (
    <div className="form-check form-switch text-end d-flex align-items-end justify-content-end">
      <input
        className="form-check-input custom-checkbox"
        type="checkbox"
        role="switch"
        checked={isChecked}
        onChange={toggleStatus}
        style={{ cursor: "pointer", width: "45px", height: "22px", transition: "0.3s ease-in-out" }}
      />
      <span
        className={`badge ms-2 ${isChecked ? "badge-success" : "badge-danger"}`}
        style={{ padding: "6px 12px", borderRadius: "8px", fontSize: "14px", fontWeight: "bold", transition: "0.3s ease-in-out" }}
      >
      </span>
    </div>
  );
});

const DefinitionsTable = () => {
  const [definitions, setDefinitions] = useState([]);
  const [newRows, setNewRows] = useState([
    { keyword: "", redirect_url: "", hits: 0, status: "active" },
  ]);
  const { canCreate, canEdit, canDelete } = usePermission("static_definitions");

  const language = useUserLanguage();
  const { translations } = useTranslations(language);
  const getText = (key, defaultValue = "") => translations[key] || defaultValue;

  const isTranslationsReady = Object.keys(translations).length > 0;

  // Arama sorgusu
  const [searchQuery, setSearchQuery] = useState("");

  // API'den verileri çek
  const fetchDefinitions = async () => {
    try {
      const response = await axios.get(BASE_URL);
      setDefinitions(response.data);
    } catch (error) {
      toast.error("Veriler alınamadı!");
    }
  };

  useEffect(() => {
    fetchDefinitions();
  }, []);

  // Arama filtrasyonu
  const filteredDefinitions = definitions.filter((def) =>
    def.keyword.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Yeni satır ekleme
  const addNewRow = () => {
    setNewRows([...newRows, { keyword: "", redirect_url: "", hits: 0, status: "active" }]);
  };

  // Form input handle
  const handleInputChange = (index, field, value) => {
    const updatedRows = [...newRows];
    updatedRows[index][field] = value;
    setNewRows(updatedRows);
  };

  // Kayıtları kaydetme
  const saveDefinitions = async () => {
    const validRows = newRows.filter(
      (row) => row.keyword.trim() && row.redirect_url.trim()
    );
    if (!validRows.length) {
      toast.warn(getText("WARNING_ADD_VALID_ROW"));
      return;
    }

    try {
      for (const row of validRows) {
        await axios.post(BASE_URL, row);
      }
      toast.success(getText("REDIRECT_SAVED"));
      setNewRows([{ keyword: "", redirect_url: "", hits: 0, status: "active" }]);
      fetchDefinitions();
    } catch (error) {
      toast.error("Yönlendirme kaydedilemedi!");
    }
  };

  const deleteDefinition = (id) => {
    confirmAlert({
      title: getText("DELETE_CONFIRM_TITLE", "Silme İşlemi"),
      message: getText("DELETE_CONFIRM_MSG", "Bu anahtar tanımını silmek istediğinizden emin misiniz?"),
      buttons: [
        {
          label: getText("YES_DELETE", "Evet, Sil"),
          onClick: async () => {
            try {
              await axios.delete(`${BASE_URL}/${id}`);
              toast.success(getText("REDIRECT_DELETED", "Tanım başarıyla silindi!"));
              fetchDefinitions();
            } catch (error) {
              toast.error(getText("ERROR_DELETE_FAILED", "Silme işlemi başarısız!"));
            }
          },
        },
        {
          label: getText("CANCEL", "İptal"),
          onClick: () =>
            toast.info(getText("DELETE_CANCELLED", "Silme işlemi iptal edildi!")),
        },
      ],
    });
  };
  

  // Tablonun kolonları
  const columns = useMemo(
    () => [
      {
        accessorKey: "id",
        header: getText("TABLE_ID_LABEL"),
        meta: { headerClassName: "text-start", className: "text-start" },
        size: 60,
      },
      {
        accessorKey: "keyword",
        header: getText("TABLE_KEYWORD_LABEL"),
        meta: { headerClassName: "text-start value-column", className: "text-start value-column" },
        size: 250,
        cell: (info) => <span>{info.getValue()}</span>,
      },
      {
        accessorKey: "redirect_url",
        header: getText("TABLE_REDIRECT_LABEL"),
        meta: { headerClassName: "text-start value-column", className: "text-start value-column" },
        size: 250,
        cell: (info) => (
          <a href={info.getValue()} target="_blank" rel="noopener noreferrer" style={{ fontWeight: "normal" }}>
            {info.getValue()}
          </a>
        ),
      },
      {
        accessorKey: "created_at",
        header: () => <div style={{ textAlign: "end" }}>{getText("TABLE_DATE_LABEL")}</div>,
        meta: { headerClassName: "text-end", className: "text-end" },
        size: 150,
        cell: (info) => {
          const rawDate = info.getValue();
          const formattedDate = new Date(rawDate).toLocaleDateString("en-GB");
          return <div style={{ textAlign: "end" }}>{formattedDate}</div>;
        },
      },
      {
        accessorKey: "status",
        header: () => <div style={{ textAlign: "end" }}>{getText("TABLE_STATUS_LABEL")}</div>,
        meta: { headerClassName: "text-end", className: "text-end" },
        size: 130,
        cell: (info) => {
          const rowData = info.row.original;
          return (
            <div style={{ textAlign: "end" }}>
              <StatusSwitch
                redirectId={rowData.id}
                currentStatus={rowData.status}
                fetchRedirects={fetchDefinitions}
              />
            </div>
          );
        },
      },
      {
        accessorKey: "actions",
        header: () => <div style={{ textAlign: "right" }}>{getText("TABLE_ACTIONS_LABEL")}</div>,
        enableSorting: false,
        meta: { headerClassName: "text-end", className: "text-end" },
        size: 180,
        cell: (info) => {
          const rowData = info.row.original;
          return (
            <div className="hstack gap-2 justify-content-end">
              {canEdit && (
                <Link
                  to={`/definition/edit/${rowData.id}`}
                  className="avatar-text avatar-md text-success bg-soft-success border-soft-success"
                  title={getText("EDIT", "Düzenle")}
                >
                  <FiEdit3 />
                </Link>
              )}
              {canDelete && (
                <button
                  className="avatar-text avatar-md text-danger bg-soft-danger border-soft-danger"
                  onClick={() => deleteDefinition(rowData.id)}
                  title={getText("DELETE")}
                >
                  <FiTrash2 />
                </button>
              )}

            </div>
          );
        },
      },
    ],
    [translations]);

    if (!isTranslationsReady) return null;

  return (
    <div className="container mt-1">
      <ToastContainer />

      <Table data={filteredDefinitions} columns={columns} />

      {/* Yeni Kayıt Ekleme Kartı */}
      {canCreate && (
      <div className="card card-body general-info">
        <div className="d-flex align-items-center justify-content-between mb-3">
          <h5 className="fw-bold text-primary mb-0">{getText("LABEL_NEW_DEFINITION")}</h5>
        </div>

        {newRows.map((row, index) => (
          <div key={index} className="border rounded p-3 mb-3">
            <div className="row g-3">
              <div className="col-md-4">
                <label className="form-label fw-bold">{getText("TABLE_KEYWORD_LABEL")}</label>
                <input
                  type="text"
                  className="form-control"
                  placeholder={getText("PLACEHOLDER_KEYWORD")}
                  value={row.keyword}
                  onChange={(e) => handleInputChange(index, "keyword", e.target.value)}
                />
              </div>
              <div className="col-md-4">
                <label className="form-label fw-bold"> {getText("TABLE_REDIRECT_LABEL")}</label>
                <input
                  type="text"
                  className="form-control"
                  placeholder={getText("PLACEHOLDER_REDIRECT_URL")}
                  value={row.redirect_url}
                  onChange={(e) => handleInputChange(index, "redirect_url", e.target.value)}
                />
              </div>
            </div>
          </div>
        ))}

        <div className="d-flex justify-content-end mb-3">
          <button className="btn btn-outline-primary" onClick={addNewRow}>
            <FiPlus /> {getText("ADD_NEW_ROW")}
          </button>
        </div>

        <div className="d-flex justify-content-center">
          <button className="btn btn-primary" onClick={saveDefinitions}>
            <FiSave /> {getText("SAVE_BTN")}
          </button>
        </div>
      </div>
      )}
    </div>
  );
};

export default DefinitionsTable;
